import React, { useState } from "react";
import { useFileStoryFlowV2 } from "../../../hooks/useFileStoryFlowV2";
import { useFileUploadAccess } from "../../../hooks/useFileUploadAccess";
import { FILE_STORY_COPY } from "../../../services/fileStory/fileStory.copy";
import { DataStoryModalShell } from "../../molecules/DataStoryModalShell";
import { tryGetActiveWorkspaceId } from "../../../services/workspace/activeWorkspace";
import { CREATE_FLOW_COPY as COPY } from "./CreateDataStoryFlowModal.copy";
import { useCreateDataStoryFlowInternalState } from "./useCreateDataStoryFlowInternalState";
import type { CreateDataStoryFlowModalProps } from "./CreateDataStoryFlowModal.types";

/**
 * Create-data-story flow — Figma 5326:88009 (recipe) → 5326:88287 (blank
 * details) / 5326:88121 (AI details) / 5326:88960 (agent details).
 *
 * A compositor and nothing else: the shell supplies the chrome, the registry
 * supplies the step, the hook supplies the state. There is deliberately no
 * `switch (stepId)` and no per-method branching here — the AI and agent frames
 * were each added as a registry entry plus a body molecule, with this file
 * untouched apart from passing `context` and the resolved `title` and
 * `primaryActionLabel` through.
 */
export const CreateDataStoryFlowModal: React.FC<
  CreateDataStoryFlowModalProps
> = ({
  open,
  onClose,
  onSubmit,
  isSubmitting = false,
  workspaceId,
  newPath = false,
  initialRecipeId,
  notice,
}) => {
  // The modal is opened from the sidebar, which has no workspace prop to give;
  // fall back to the active workspace rather than making every call site thread
  // one through. An empty id simply leaves the picker lists empty.
  const activeWorkspaceId = workspaceId ?? tryGetActiveWorkspaceId() ?? "";

  const {
    step,
    title,
    primaryActionLabel,
    values,
    workspaceName,
    patchValues,
    goNext,
    canContinue,
    context,
  } = useCreateDataStoryFlowInternalState({
    open,
    onSubmit,
    workspaceId: activeWorkspaceId,
    newPath,
    initialRecipeId,
  });

  const { Body } = step;
  const { canUpload } = useFileUploadAccess();
  const startFileStory = useFileStoryFlowV2(activeWorkspaceId);
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileBusy, setFileBusy] = useState(false);
  const pickFile = (file: File) => {
    setFileBusy(true);
    setFileError(null);
    const goal = (values as { goal?: string } | undefined)?.goal ?? "";
    void startFileStory(file, goal.trim()).then((failure) => {
      setFileBusy(false);
      setFileError(failure);
      if (!failure) onClose();
    });
  };
  const bodyContext = newPath
    ? {
        ...context,
        fileUpload: {
          onPick: pickFile,
          disabledReason: canUpload ? undefined : FILE_STORY_COPY.adminOnlyHint,
          error: fileError,
          isBusy: fileBusy,
        },
      }
    : context;

  return (
    <DataStoryModalShell
      open={open}
      title={title}
      subtitle={step.subtitle}
      onClose={onClose}
      primaryActionLabel={primaryActionLabel}
      onPrimaryAction={goNext}
      primaryActionDisabled={!canContinue}
      isSubmitting={isSubmitting}
      bodyPadding={step.bodyPadding}
      bodyOverflowY={step.bodyOverflowY}
      footerNote={workspaceName ? COPY.workspace(workspaceName) : undefined}
      secondaryActionLabel={COPY.cancel}
      onSecondaryAction={onClose}
      notice={notice}
    >
      <Body values={values} onChange={patchValues} context={bodyContext} />
    </DataStoryModalShell>
  );
};

export default CreateDataStoryFlowModal;
